import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setAuthToken, getAuthToken } from "@/lib/api";
import { ALL_CONTENT_KEYS, CONTENT_PAGES } from "@/lib/permissions";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staffPages, setStaffPages] = useState(null);
  const [hideMoney, setHideMoney] = useState(() => {
    try { return localStorage.getItem("vinyl_hide_money") === "1"; } catch { return false; }
  });

  const toggleHideMoney = useCallback(() => {
    setHideMoney((prev) => {
      const next = !prev;
      try { localStorage.setItem("vinyl_hide_money", next ? "1" : "0"); } catch {}
      return next;
    });
  }, []);

  const maskMoney = useCallback((formatted) => (hideMoney ? "$••••" : formatted), [hideMoney]);

  const checkAuth = useCallback(async () => {
    if (!getAuthToken()) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setAuthToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Staff tab access is admin-configurable; load it for staff accounts only.
  useEffect(() => {
    if (!user || user.role !== "staff") return;
    api.get("/permissions").then(({ data }) => setStaffPages(data.staff_pages)).catch(() => {});
  }, [user]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setAuthToken(data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setAuthToken(null);
    setUser(false);
    setStaffPages(null);
  };

  const isAdmin = !!user && user.role === "admin";
  const isViewer = !!user && user.role === "viewer";
  const isStaff = !!user && user.role === "staff";
  const canSeePricing = isAdmin; // pricing / stock value is admin-only
  const isAuthed = !!user;

  let allowedPageKeys = [];
  if (user) {
    if (isAdmin) {
      allowedPageKeys = [...ALL_CONTENT_KEYS, "settings", "update-requests", "activity-log", "product-radar"];
    } else if (isViewer) {
      allowedPageKeys = ["dashboard", "stock-on-hand"];
    } else {
      const base = staffPages || ALL_CONTENT_KEYS;
      allowedPageKeys = Array.from(new Set([...base, "stock-on-hand", "per-unit-products", "scan", "stock-take", "to-order", "archive", "settings"]));
    }
  }

  const canAccessPage = (key) => {
    if (!key) return true; // unknown/root paths remain accessible to authed users
    return allowedPageKeys.includes(key);
  };

  let landingPath = "/dashboard";
  if (user && !isViewer) {
    if (allowedPageKeys.includes("dashboard")) {
      landingPath = "/dashboard";
    } else {
      const first = CONTENT_PAGES.find((p) => allowedPageKeys.includes(p.key));
      landingPath = first ? first.path : "/dashboard";
    }
  }

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout,
      isAdmin, isViewer, isStaff, canSeePricing, isAuthed,
      allowedPageKeys, canAccessPage, landingPath,
      hideMoney, toggleHideMoney, maskMoney,
      refresh: checkAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
